import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { AutomationsWorkflowGraphic } from '@WebComponents/svgs/graphics/AutomationsWorkflowGraphic';
import { CircularAddIcon } from '@WebComponents/svgs/icons/CircularAddIcon';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ILocationState, InternalAccountIds } from '../../../models';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../models/AppState';
import { SupportedIndustries } from '../../../models/MessageTemplates';
import { getIndustryName } from '../../../models/UiUtils';
import {
	AutomationTemplateViewModel,
	AutomationTemplatesViewModel,
	AutomationsOnHoldViewModel,
	IAccount,
	IOperationResultNoValue,
	Industry,
	ResourceSelectorId,
} from '../../../viewmodels/AppViewModels';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { AutomationCard } from '../../components/automation/AutomationCard';
import { AutomationReportingCard } from '../../components/automation/AutomationReportingCard';
import { AutomationsPendingCard } from '../../components/automation/AutomationsPendingCard';
import { AutomationFilters } from '../../components/AutomationFilters';
import { LoadingLogo } from '../../components/LoadingLogo';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { DefaultSelectBox, ISelectBoxOption } from '../../components/SelectBox';
import { darkGrayFontColor } from '../../styles/colors';
import { baseStyleSheet, bs } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		RouteComponentProps<any>,
		IUserSessionComponentProps,
		IErrorMessageComponentProps,
		IFullscreenModalComponentProps {
	account?: IAccount;
	automationTemplates?: AutomationTemplatesViewModel;
	className?: string;
	createAutomationButtonStyles?: StyleDeclarationValue[];
	headerStyles?: StyleDeclarationValue[];
	hideAppBarHeader?: boolean;
	hideDescription?: boolean;
	hideOnHoldAutomations?: boolean;
	onRenderHeader?(): React.ReactNode;
	/** Return true if consumer will handle creation */
	onRequestCreate?(): boolean;
	/** Return true if consumer will handle editing */
	onRequestEdit?(automationTemplate: AutomationTemplateViewModel): boolean;
	styles?: StyleDeclarationValue[];
}

interface IState {
	industry?: Industry;
	loading?: boolean;
	isReporting?: boolean;
	searchText?: string;
	selectedCreatorId?: string | null;
	searchInput?: string; // immediate input state for debouncing
}

const IndustrySelectBoxOptions = SupportedIndustries.map<ISelectBoxOption<Industry>>(x => {
	return {
		title: getIndustryName(x),
		value: x as Industry,
	};
});

class _Automations extends React.Component<IProps, IState> {
	private mAutomationTemplates: AutomationTemplatesViewModel;
	private automationsOnHold: AutomationsOnHoldViewModel;
	private searchDebounceTimer: NodeJS.Timeout | null = null;
	constructor(props: IProps) {
		super(props);

		makeObservable<_Automations, 'automationsOnHold' | 'creatorOptions'>(this, {
			automationsOnHold: observable.ref,
			creatorOptions: computed,
		});

		this.state = {
			isReporting: props.match.path.includes('reporting'),
			loading: true,
			searchText: '',
			searchInput: '',
			selectedCreatorId: null,
		};

		this.mAutomationTemplates = props.automationTemplates || new AutomationTemplatesViewModel(props.userSession);

		this.automationsOnHold = !props.hideOnHoldAutomations && new AutomationsOnHoldViewModel(props.userSession);
	}

	public componentDidMount() {
		const { logApiError, logEvent, errorMessages, location, hideOnHoldAutomations } = this.props;
		const promise = this.mAutomationTemplates.load({
			...AutomationTemplatesViewModel.defaultLoadOptions,
			forAnyTrigger: true,
		});
		if (promise) {
			logEvent('TemplatesLoad');
			promise
				.then(() => {
					this.setState({
						loading: false,
					});
				})
				.catch((error: IOperationResultNoValue) => {
					logApiError('TemplatesLoad-Error', error);

					errorMessages.pushApiError(error);
					this.setState({
						loading: false,
					});
				});
		}
		if (location.state === 'pending' && !hideOnHoldAutomations) {
			const { fullscreenModal, userSession, account: accountOverride } = this.props;
			const account = accountOverride || userSession?.account;
			if (account?.features?.automation?.enabled) {
				this.automationsOnHold
					.loadAutomationsOnHold()
					?.then(() => {
						const locationState: ILocationState<AutomationsOnHoldViewModel, any> = {
							viewModel: this.automationsOnHold,
						};

						fullscreenModal.history.push({
							pathname: `/automationsonhold/${ResourceSelectorId.AutomationsOnHold}`,
							state: locationState,
						});
					})
					?.catch((err: IOperationResultNoValue) => {
						logApiError('LoadAutomationsOnHold-Error', err);

						errorMessages.pushApiError(err);
					});
			}
		}
	}

	private getFilteredTemplates = (templates: 'active' | 'inactive' | 'industry'): AutomationTemplateViewModel[] => {
		const { searchText, selectedCreatorId } = this.state;

		try {
			// Filter automation templates based on search text and creator
			return this.mAutomationTemplates[`${templates}`].filter((template: AutomationTemplateViewModel) => {
				// Search text filter - case insensitive search on automation name
				const searchMatch =
					!searchText || (template.name && template.name.toLowerCase().includes(searchText.toLowerCase()));

				// Creator filter - match by creator ID
				const creatorMatch = !selectedCreatorId || template.creator?.id === selectedCreatorId;

				return searchMatch && creatorMatch;
			});
		} catch (error) {
			console.error('Error filtering automation templates:', error);
			// Fallback to unfiltered templates
			return this.mAutomationTemplates[`${templates}`] || [];
		}
	};

	private getAutomationTemplates = (templates: 'active' | 'inactive' | 'industry') => {
		const { isReporting } = this.state;
		const filteredTemplates = this.getFilteredTemplates(templates);

		return (
			<>
				{filteredTemplates.map((x: AutomationTemplateViewModel) => {
					return !isReporting ? (
						<AutomationCard
							automationTemplate={x}
							automationTemplates={this.mAutomationTemplates}
							key={x.id}
							onRequestEdit={this.onRequestEdit(x)}
						/>
					) : (
						<AutomationReportingCard
							automationTemplate={x}
							automationTemplates={this.mAutomationTemplates}
							key={x.id}
						/>
					);
				})}
			</>
		);
	};

	private get creatorOptions(): { title: string; value: string | null }[] {
		try {
			const creators = new Map<string, string>();

			// Add "Show all creators" option
			const options: { title: string; value: string | null }[] = [{ title: 'Show all creators', value: null }];

			// Get all automation templates from all categories
			const allTemplates = [
				...this.mAutomationTemplates.active,
				...this.mAutomationTemplates.inactive,
				...this.mAutomationTemplates.industry,
			];

			// Extract unique creators from all automation templates
			allTemplates.forEach(template => {
				if (template.creator?.id && !creators.has(template.creator.id)) {
					const creatorName =
						template.creator.firstName && template.creator.lastName
							? `${template.creator.firstName} ${template.creator.lastName}`.trim()
							: template.creator.email || 'Unknown Creator';

					creators.set(template.creator.id, creatorName);
					options.push({
						title: creatorName,
						value: template.creator.id,
					});
				}
			});

			// Sort creator options alphabetically by name (excluding "Show all creators")
			const sortedCreatorOptions = options.slice(1).sort((a, b) => {
				const titleA = typeof a.title === 'string' ? a.title : '';
				const titleB = typeof b.title === 'string' ? b.title : '';
				return titleA.localeCompare(titleB);
			});

			const result = [options[0], ...sortedCreatorOptions];

			// Fallback for empty options
			return result.length > 0 ? result : [{ title: 'Show all creators', value: null }];
		} catch (error) {
			console.error('Error generating creator options:', error);
			// Fallback for errors
			return [{ title: 'Show all creators', value: null }];
		}
	}

	private handleSearchTextChange = (searchInput: string) => {
		// Update immediate input state for responsive UI
		this.setState({ searchInput });

		// Clear existing timer
		if (this.searchDebounceTimer) {
			clearTimeout(this.searchDebounceTimer);
		}

		// Set new timer to update search state after 300ms
		this.searchDebounceTimer = setTimeout(() => {
			this.setState({ searchText: searchInput });
		}, 300);
	};

	public componentWillUnmount() {
		if (this.searchDebounceTimer) {
			clearTimeout(this.searchDebounceTimer);
		}
	}

	private handleCreatorFilterChange = (creatorId: string | null) => {
		this.setState({ selectedCreatorId: creatorId });
	};

	private renderFilters = () => {
		const { searchInput, selectedCreatorId } = this.state;

		return (
			<AutomationFilters
				searchText={searchInput || ''}
				selectedCreatorId={selectedCreatorId || null}
				creatorOptions={this.creatorOptions}
				onSearchTextChange={this.handleSearchTextChange}
				onCreatorFilterChange={this.handleCreatorFilterChange}
			/>
		);
	};

	public render() {
		const { styles, userSession } = this.props;
		const { isReporting, loading } = this.state;
		return (
			<div className={css(styleSheet.container, ...(styles || []))}>
				{!isReporting ? this.renderHeaders() : null}
				<div className={css(styleSheet.body)}>
					{loading ? (
						<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter)}>
							<LoadingLogo size={100} />
						</div>
					) : (
						<>
							{!isReporting && this.renderFilters()}
							<div className={css(styleSheet.cardGrid)}>
								<h3 className={css(styleSheet.cardGridTitle)} style={isReporting ? { color: darkGrayFontColor } : {}}>
									Active Automations
								</h3>
								<div className={css(styleSheet.cardGridContent)}>{this.getAutomationTemplates('active')}</div>
							</div>
							<div className={css(styleSheet.cardGrid)}>
								<h3 className={css(styleSheet.cardGridTitle)} style={isReporting ? { color: darkGrayFontColor } : {}}>
									Inactive Automations
								</h3>
								<div className={css(styleSheet.cardGridContent)}>{this.getAutomationTemplates('inactive')}</div>
							</div>

							{(this.mAutomationTemplates?.industry?.length > 0 || !!InternalAccountIds.has(userSession.account.id)) &&
							!isReporting ? (
								<div className={css(styleSheet.cardGrid)}>
									<h3 className={css(styleSheet.cardGridTitle, styleSheet.sampleAutomationsSectionTitle)}>
										<span>Sample Automations</span>
										{this.renderIndustrySelector()}
									</h3>
									<div className={css(styleSheet.cardGridContent)}>{this.getAutomationTemplates('industry')}</div>
								</div>
							) : null}
						</>
					)}
				</div>
				<AppDrawer>
					<StickyAppDrawerContent value='ai-assistant'>
						<DefaultUniversalAssistantChat />
					</StickyAppDrawerContent>
					<AppDrawerToolbar>
						<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
					</AppDrawerToolbar>
				</AppDrawer>
			</div>
		);
	}

	private renderHeaders() {
		const { loading } = this.state;
		const { hideOnHoldAutomations, hideAppBarHeader, hideDescription, headerStyles = [] } = this.props;
		return (
			<>
				{!hideAppBarHeader && (
					<MultiContainerHeader appBarHeader={<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Automations</h1>} />
				)}
				<AutomationsPendingCard shouldHide={loading || hideOnHoldAutomations} />
				<div className={css(styleSheet.header, ...headerStyles)}>
					{!hideDescription && (
						<>
							<div className={css(styleSheet.description)}>
								<div className={css(styleSheet.descriptionTitle)}>What is an automation?</div>
								<div>
									An automation (in Levitate) can send a series of emails for you, create a series of action items that
									you find yourself repeating constantly, or a mixture of both! We&apos;re here to automate.
								</div>
							</div>
							<AutomationsWorkflowGraphic className={css(styleSheet.headerWorkflowGraphic)} />
						</>
					)}
					{this.renderCreateNewAutomationButton()}
				</div>
			</>
		);
	}

	private renderCreateNewAutomationButton() {
		const { createAutomationButtonStyles = [] } = this.props;
		const { loading } = this.state;

		const button = (
			<button
				className={css(styleSheet.createNewButton, ...createAutomationButtonStyles)}
				disabled={!!loading}
				onClick={this.onCreateButtonClicked}
			>
				<CircularAddIcon />
				<span>
					Create New
					<br />
					Automation
				</span>
			</button>
		);

		if (this.mAutomationTemplates.canCreate) {
			return button;
		}

		return null;
	}

	private renderIndustrySelector() {
		const { userSession } = this.props;

		if (!InternalAccountIds.has(userSession?.account?.id)) {
			return null;
		}
		const { industry } = this.state;
		const selectedOption =
			IndustrySelectBoxOptions.find(
				x => x.value === (industry ? industry : userSession.account?.toJs().additionalInfo.industry)
			) || IndustrySelectBoxOptions.find(x => x.value === Industry.Miscellaneous);
		return (
			<DefaultSelectBox
				className={css(styleSheet.levTemplatesHeaderIndustrySelector)}
				onSelectionChanged={this.onIndustryChanged}
				options={IndustrySelectBoxOptions}
				optionStyles={[styleSheet.levTemplatesHeaderIndustrySelectorOption]}
				selectedOption={selectedOption}
				triggerStyles={[styleSheet.levTemplatesHeaderIndustrySelectorOption]}
			/>
		);
	}

	private onRequestEdit = (template: AutomationTemplateViewModel) => () => {
		return this.props.onRequestEdit?.(template) || false;
	};

	private onIndustryChanged = (option: ISelectBoxOption<Industry>) => {
		const { logApiError, logEvent, errorMessages } = this.props;
		this.setState(
			{
				industry: option.value,
			},
			() => {
				const promise = this.mAutomationTemplates.loadTemplatesForIndustry(option.value);
				if (promise) {
					logEvent('LoadIndustryTemplates', { industry: option.value });
					promise.catch((error: IOperationResultNoValue) => {
						logApiError('LoadIndustryTemplates-Error', error);

						errorMessages.pushApiError(error);
					});
				}
			}
		);
	};

	private onCreateButtonClicked = () => {
		const { history, match, logInput, onRequestCreate } = this.props;
		if (!onRequestCreate?.()) {
			logInput('CreateAutomation', 'Click');
			history?.push({
				pathname: `${match.url}/new`,
			});
		}
	};
}

const AutomationsAsObserver = observer(_Automations);
const AutomationsWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey
)(AutomationsAsObserver);
export const Automations = withRouter(withEventLogging(AutomationsWithContext, 'Automations'));
